import type { ReactNode } from 'react'
import { useTenant } from './TenantProvider'
import type { CustomerModuleCode } from './service-access-api'

function RestrictionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="restriction-card" role="status">
      <p className="eyebrow">Swell AI access</p>
      <h1>{title}</h1>
      <div className="restriction-copy">{children}</div>
    </section>
  )
}

function OrganisationAccessPending() {
  return (
    <RestrictionCard title="Checking your organisation access">
      <p>We are confirming the Swell areas available to you.</p>
    </RestrictionCard>
  )
}

function OrganisationAccessError() {
  return (
    <RestrictionCard title="We could not confirm your access">
      <p>Something prevented Swell from confirming your organisation access. Try refreshing the page. If the problem continues, contact your organisation administrator.</p>
    </RestrictionCard>
  )
}

export function CustomerServiceRoute({ children }: { children: ReactNode }) {
  const { current, servicePermitted, loading, error } = useTenant()

  if (loading) return <OrganisationAccessPending />
  if (error) return <OrganisationAccessError />
  if (!current) return <>{children}</>
  if (servicePermitted) return <>{children}</>

  const message = current.service.customerMessage ??
    (current.service.status === 'provisioning'
      ? 'Your organisation access is being prepared.'
      : 'Swell AI is not currently available for this organisation.')

  return (
    <RestrictionCard title="Swell AI access is currently restricted">
      <p><strong>{current.organisation.name}</strong></p>
      <p>{message}</p>
      <p className="muted">If you belong to another organisation, you can switch organisation from the navigation.</p>
    </RestrictionCard>
  )
}

export function CustomerModuleRoute({ module, children }: { module: CustomerModuleCode; children: ReactNode }) {
  const { current, servicePermitted, hasModule, loading, error } = useTenant()

  if (loading) return <OrganisationAccessPending />
  if (error) return <OrganisationAccessError />
  if (!current) return <>{children}</>
  if (!servicePermitted) return <CustomerServiceRoute>{children}</CustomerServiceRoute>
  if (hasModule(module)) return <>{children}</>

  return (
    <RestrictionCard title="This area is not available for your organisation">
      <p><strong>{current.organisation.name}</strong> does not currently have access to this part of Swell AI.</p>
      <p className="muted">Your other available areas remain in the navigation.</p>
    </RestrictionCard>
  )
}
